import React from 'react';
import moment from 'moment-timezone';
import {
  Modal,
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  AddAlert as AddAlertIcon,
} from '@mui/icons-material';
import axios from 'axios';
import SimpleCustomTable from '../table/SimpleCustomTable';
import { SimpleColumn } from '../table/interface';
import PermissionButton from './PermissionButton';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from '../table/iconActionColumnSizing';

const historyModalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1100,
  height: 620,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

interface ClientChangeHistoryModalProps {
  open: boolean;
  onClose: () => void;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  canManageBackupAlert?: boolean;
  onBackupAlertChange?: (clientId: number, active: boolean) => void;
  clientData?: {
    id?: number;
    backup_alert_active?: boolean;
    backups?: any[];
  } | null;
}

const ClientChangeHistoryModal: React.FC<ClientChangeHistoryModalProps> = ({
  open,
  onClose,
  showAlert,
  showLoading,
  hideLoading,
  canManageBackupAlert = false,
  onBackupAlertChange,
  clientData,
}) => {
  const backups = Array.isArray(clientData?.backups) ? clientData!.backups! : [];
  const backupAlertActive = Boolean(clientData?.backup_alert_active);

  const handleToggleBackupAlert = async () => {
    const clientId = Number(clientData?.id || 0);
    if (!Number.isFinite(clientId) || clientId <= 0) {
      showAlert('Cliente inválido para actualizar alerta.', 'warning');
      return;
    }

    const nextActive = !backupAlertActive;

    try {
      showLoading();
      const { data } = await axios.put(`/clients/${clientId}/backup-alert`, { active: nextActive });
      const refreshedClient = data?.client;
      const finalActive = Boolean(refreshedClient?.backup_alert_active ?? nextActive);
      onBackupAlertChange?.(clientId, finalActive);
      showAlert(finalActive ? 'Alerta activada correctamente.' : 'Alerta desactivada correctamente.', 'success');
    } catch (error) {
      console.error('Error al actualizar la alerta del cliente:', error);
      showAlert('No fue posible actualizar la alerta del cliente.', 'error');
    } finally {
      hideLoading();
    }
  };

  const backupColumns: SimpleColumn[] = [
    { field: 'id', headerName: 'ID', minWidth: 70, maxWidth: 90 },
    { field: 'idnumber', headerName: 'Cédula', minWidth: 110, maxWidth: 140 },
    { field: 'fullname', headerName: 'Cliente', minWidth: 180, width: 200, wrapText: true },
    { field: 'phone', headerName: 'Teléfono', minWidth: 110, maxWidth: 140 },
    { field: 'cellphone', headerName: 'Celular', minWidth: 110, maxWidth: 140 },
    { field: 'mainaddress', headerName: 'Dirección Principal', minWidth: 180, width: 220, wrapText: true },
    { field: 'createdAt', headerName: 'Fecha de Creación', minWidth: 160, width: 180 },
    {
      field: 'opt_alert',
      headerName: '',
      ...iconActionColumnSizing(false),
      renderCell: () =>
        backups.length > 0 ? (
          <PermissionButton
            allowed={canManageBackupAlert}
            deniedTooltip="Sin permiso para gestionar la alerta"
            tooltip={backupAlertActive ? 'Desactivar alerta' : 'Activar alerta'}
            variant="contained"
            color="primary"
            sx={simpleTableIconButtonSx}
            onClick={handleToggleBackupAlert}
          >
            <AddAlertIcon
              sx={{ color: backupAlertActive ? '#ff9800' : '#9e9e9e' }}
              fontSize="small"
            />
          </PermissionButton>
        ) : null,
    },
  ];

  const backupRows = backups.map((backup, index) => ({
    id: backup.id ?? `${backup.idnumber || 'backup'}-${index}`,
    idnumber: backup.idnumber || '',
    fullname: backup.fullname || '',
    phone: backup.phone || '',
    cellphone: backup.cellphone || '',
    mainaddress: backup.mainaddress || '',
    createdAt: backup.createdAt
      ? moment(backup.createdAt).tz('America/Bogota').format('YYYY/MM/DD HH:mm')
      : '',
  }));

  return (
    <Modal open={open} onClose={onClose}>
      <Paper sx={historyModalStyle}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
            px: 2,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ color: 'inherit' }}>
            Historial de Cambios
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white' }}
            aria-label="cerrar"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
          <SimpleCustomTable
            rows={backupRows}
            columns={backupColumns}
            maxHeight={430}
            rowHeight={24}
            autoRowHeight
            showBorders
            showCount
            exportCsv
            exportFileName="historial_cambios_cliente.csv"
            wrapCellContent
            columnGroups={[{ header: 'Opciones', fields: ['opt_alert'] }]}
          />
        </Box>

        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={onClose}>Cerrar</Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ClientChangeHistoryModal;
